import React, { useState, useEffect } from "react";
import axiosInstance from "../../../axiosInstance";
import { toast } from "react-toastify";
import IconButton from "../../../components/IconButton/IconButton";
import ButtonWithIcon from "../../../components/ButtonwithIcon/ButtonWithIcon";
import LoadingScreen from "../../../components/LoadingScreen/LoadingScreen";
import "./EmployeeLeaveBalancePopup.scss";

function EmployeeLeaveBalancePopup({ isOpen, onClose }) {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showDetailPopup, setShowDetailPopup] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (isOpen) {
      fetchEmployeeLeaveBalances();
    }
  }, [isOpen]);

  const fetchEmployeeLeaveBalances = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}/employee-leave-balance/all`
      );
      if (response.data?.data) {
        setEmployees(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching employee leave balances:", error);
      toast.error("Failed to fetch employee leave balances");
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (employee) => {
    setSelectedEmployee(employee);
    setShowDetailPopup(true);
  };

  const handleCloseDetailPopup = () => {
    setShowDetailPopup(false);
    setSelectedEmployee(null);
  };

  const handleUpdateSuccess = () => {
    fetchEmployeeLeaveBalances();
    handleCloseDetailPopup();
  };

  const filteredEmployees = employees.filter((employee) =>
    employee.fullName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <>
      <div className="popup-overlay" onClick={onClose}>
        <div
          className="employee-leave-popup"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="employee-leave-popup__header">
            <h2>Employee Leave Balances</h2>
            <button
              className="employee-leave-popup__close"
              onClick={onClose}
              aria-label="Close"
            >
              ×
            </button>
          </div>

          <div className="employee-leave-popup__search">
            <input
              type="text"
              placeholder="Search by employee name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="employee-leave-popup__search-input"
            />
            {searchTerm && (
              <button
                className="employee-leave-popup__search-clear"
                onClick={() => setSearchTerm("")}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>

          <div className="employee-leave-popup__content">
            {loading ? (
              <LoadingScreen />
            ) : filteredEmployees.length > 0 ? (
              <div className="employee-leave-table">
                <table>
                  <thead>
                    <tr>
                      <th>Employee Name</th>
                      <th>Total Leaves</th>
                      <th>Available Leaves</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEmployees.map((employee) => {
                      const totalLeaves =
                        employee.leaveHistoryManagementResponseDTOS?.reduce(
                          (sum, leave) => sum + (leave.totalLeaves || 0),
                          0
                        ) || 0;
                      const availableLeaves =
                        employee.leaveHistoryManagementResponseDTOS?.reduce(
                          (sum, leave) => sum + (leave.availableLeaves || 0),
                          0
                        ) || 0;

                      return (
                        <tr key={employee.empId}>
                          <td>
                            <div className="employee-info">
                              <span className="employee-name">
                                {employee.fullName}
                              </span>
                            </div>
                          </td>
                          <td>
                            <span className="leave-count">{totalLeaves}</span>
                          </td>
                          <td>
                            <span className="leave-count available">
                              {availableLeaves}
                            </span>
                          </td>
                          <td>
                            <IconButton
                              variant="edit"
                              icon="iconamoon:edit-light"
                              onClick={() => handleEditClick(employee)}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="employee-leave-popup__empty">
                <p>
                  {searchTerm
                    ? `No employees found matching "${searchTerm}"`
                    : "No employee leave data available"}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {showDetailPopup && selectedEmployee && (
        <EmployeeLeaveDetailPopup
          employee={selectedEmployee}
          onClose={handleCloseDetailPopup}
          onSuccess={handleUpdateSuccess}
        />
      )}
    </>
  );
}

function EmployeeLeaveDetailPopup({ employee, onClose, onSuccess }) {
  const [leaveData, setLeaveData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newLeaveType, setNewLeaveType] = useState({
    leaveTypeName: "",
    totalLeaves: 0,
    availableLeaves: 0,
    carryForward: false,
    carryForwardDays: 0,
    noOfYears: 0,
    encashment: false,
    encashmentDays: 0,
    optionalHoliday: false,
  });

  useEffect(() => {
    if (employee?.leaveHistoryManagementResponseDTOS) {
      setLeaveData(
        employee.leaveHistoryManagementResponseDTOS.map((leave) => ({
          ...leave,
          isExisting: true,
        }))
      );
    }
  }, [employee]);

  const handleInputChange = (index, field, value) => {
    setLeaveData((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: value,
      };
      return updated;
    });
  };

  const handleNewLeaveTypeChange = (field, value) => {
    setNewLeaveType((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddLeaveType = () => {
    if (!newLeaveType.leaveTypeName.trim()) {
      toast.error("Please enter a leave type name");
      return;
    }

    const newLeave = {
      empLeaveTypeId: 0,
      leaveTypeId: 0,
      leaveTypeName: newLeaveType.leaveTypeName,
      employeeId: employee.empId,
      totalLeaves: parseInt(newLeaveType.totalLeaves) || 0,
      availableLeaves: parseInt(newLeaveType.availableLeaves) || 0,
      carryForward: newLeaveType.carryForward,
      carryForwardDays: newLeaveType.carryForward
        ? parseInt(newLeaveType.carryForwardDays) || 0
        : 0,
      noOfYears: parseInt(newLeaveType.noOfYears) || 0,
      encashment: newLeaveType.encashment,
      encashmentDays: newLeaveType.encashment
        ? parseInt(newLeaveType.encashmentDays) || 0
        : 0,
      optionalHoliday: newLeaveType.optionalHoliday,
      isExisting: false,
    };

    setLeaveData((prev) => [...prev, newLeave]);
    setShowAddForm(false);
    setNewLeaveType({
      leaveTypeName: "",
      totalLeaves: 0,
      availableLeaves: 0,
      carryForward: false,
      carryForwardDays: 0,
      noOfYears: 0,
      encashment: false,
      encashmentDays: 0,
      optionalHoliday: false,
    });
    toast.success(`${newLeaveType.leaveTypeName} added`);
  };

  const handleDeleteLeaveType = async (index) => {
    const leave = leaveData[index];

    if (
      !window.confirm(`Are you sure you want to delete ${leave.leaveTypeName}?`)
    ) {
      return;
    }

    if (!leave.isExisting || leave.empLeaveTypeId === 0) {
      setLeaveData((prev) => prev.filter((_, i) => i !== index));
      toast.info(`${leave.leaveTypeName} removed`);
      return;
    }

    setLoading(true);
    try {
      await axiosInstance.delete(
        `${process.env.REACT_APP_API_BASE_URL}/employee-leave-balance/${leave.empLeaveTypeId}`
      );

      setLeaveData((prev) => prev.filter((_, i) => i !== index));
      toast.success(`${leave.leaveTypeName} deleted successfully`);
    } catch (error) {
      console.error("Error deleting leave type:", error);
      toast.error(
        error.response?.data?.message || "Failed to delete leave type"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAll = async () => {
    setLoading(true);
    try {
      const bulkUpdateData = leaveData.map((leave) => ({
        empLeaveTypeId: leave.empLeaveTypeId || 0,
        leaveTypeId: leave.leaveTypeId || 0,
        leaveTypeName: leave.leaveTypeName,
        employeeId: employee.empId,
        totalLeaves: parseInt(leave.totalLeaves) || 0,
        availableLeaves: parseInt(leave.availableLeaves) || 0,
        carryForward: leave.carryForward || false,
        carryForwardDays: leave.carryForward
          ? parseInt(leave.carryForwardDays) || 0
          : 0,
        noOfYears: parseInt(leave.noOfYears) || 0,
        encashment: leave.encashment || false,
        encashmentDays: leave.encashment
          ? parseInt(leave.encashmentDays) || 0
          : 0,
        optionalHoliday: leave.optionalHoliday || false,
      }));

      await axiosInstance.put(
        `${process.env.REACT_APP_API_BASE_URL}/employee-leave-balance/bulkupdate`,
        bulkUpdateData
      );

      toast.success("All leave balances updated successfully");
      onSuccess();
    } catch (error) {
      console.error("Error updating leave balances:", error);
      toast.error(
        error.response?.data?.message || "Failed to update leave balances"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="popup-overlay" onClick={onClose}>
      <div
        className="employee-leave-detail-popup"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="employee-leave-detail-popup__header">
          <div>
            <h2>{employee.fullName}</h2>
            <p className="employee-subtitle">Manage Leave Types & Balances</p>
          </div>
          <button
            className="employee-leave-detail-popup__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="employee-leave-detail-popup__content">
          {leaveData.length > 0 ? (
            <div className="leave-details-grid">
              {leaveData.map((leave, index) => (
                <div
                  key={`${leave.empLeaveTypeId}-${index}`}
                  className="leave-detail-card"
                >
                  <div className="leave-detail-card__header">
                    <h3 className="leave-detail-card__title">
                      {leave.leaveTypeName}
                      {!leave.isExisting && (
                        <span className="new-badge">NEW</span>
                      )}
                    </h3>
                    <IconButton
                      variant="delete"
                      icon="mdi:delete"
                      onClick={() => handleDeleteLeaveType(index)}
                    />
                  </div>

                  <div className="leave-detail-card__form">
                    <div className="form-row">
                      <div className="form-group">
                        <label>Total Leaves</label>
                        <input
                          type="number"
                          value={leave.totalLeaves}
                          onChange={(e) =>
                            handleInputChange(
                              index,
                              "totalLeaves",
                              e.target.value
                            )
                          }
                          min="0"
                        />
                      </div>
                      <div className="form-group">
                        <label>Available Leaves</label>
                        <input
                          type="number"
                          value={leave.availableLeaves}
                          onChange={(e) =>
                            handleInputChange(
                              index,
                              "availableLeaves",
                              e.target.value
                            )
                          }
                          min="0"
                        />
                      </div>
                      <div className="form-group">
                        <label>No. of Years</label>
                        <input
                          type="number"
                          value={leave.noOfYears}
                          onChange={(e) =>
                            handleInputChange(
                              index,
                              "noOfYears",
                              e.target.value
                            )
                          }
                          min="0"
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group checkbox-group">
                        <label>
                          <input
                            type="checkbox"
                            checked={leave.carryForward}
                            onChange={(e) =>
                              handleInputChange(
                                index,
                                "carryForward",
                                e.target.checked
                              )
                            }
                          />
                          Carry Forward
                        </label>
                      </div>
                      {leave.carryForward && (
                        <div className="form-group">
                          <label>Carry Forward Days</label>
                          <input
                            type="number"
                            value={leave.carryForwardDays}
                            onChange={(e) =>
                              handleInputChange(
                                index,
                                "carryForwardDays",
                                e.target.value
                              )
                            }
                            min="0"
                          />
                        </div>
                      )}
                    </div>

                    <div className="form-row">
                      <div className="form-group checkbox-group">
                        <label>
                          <input
                            type="checkbox"
                            checked={leave.encashment}
                            onChange={(e) =>
                              handleInputChange(
                                index,
                                "encashment",
                                e.target.checked
                              )
                            }
                          />
                          Encashment
                        </label>
                      </div>
                      {leave.encashment && (
                        <div className="form-group">
                          <label>Encashment Days</label>
                          <input
                            type="number"
                            value={leave.encashmentDays}
                            onChange={(e) =>
                              handleInputChange(
                                index,
                                "encashmentDays",
                                e.target.value
                              )
                            }
                            min="0"
                          />
                        </div>
                      )}
                    </div>

                    <div className="form-row">
                      <div className="form-group checkbox-group">
                        <label>
                          <input
                            type="checkbox"
                            checked={leave.optionalHoliday}
                            onChange={(e) =>
                              handleInputChange(
                                index,
                                "optionalHoliday",
                                e.target.checked
                              )
                            }
                          />
                          Optional Holiday
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="employee-leave-detail-popup__empty">
              <p>No leave types assigned yet</p>
            </div>
          )}

          {showAddForm && (
            <div className="add-leave-form">
              <h3>Add New Leave Type</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Leave Type Name *</label>
                  <input
                    type="text"
                    value={newLeaveType.leaveTypeName}
                    onChange={(e) =>
                      handleNewLeaveTypeChange("leaveTypeName", e.target.value)
                    }
                    placeholder="e.g., Sick Leave, Casual Leave"
                  />
                </div>

                <div className="form-group">
                  <label>Total Leaves</label>
                  <input
                    type="number"
                    value={newLeaveType.totalLeaves}
                    onChange={(e) =>
                      handleNewLeaveTypeChange("totalLeaves", e.target.value)
                    }
                    min="0"
                  />
                </div>

                <div className="form-group">
                  <label>Available Leaves</label>
                  <input
                    type="number"
                    value={newLeaveType.availableLeaves}
                    onChange={(e) =>
                      handleNewLeaveTypeChange(
                        "availableLeaves",
                        e.target.value
                      )
                    }
                    min="0"
                  />
                </div>

                <div className="form-group">
                  <label>No. of Years</label>
                  <input
                    type="number"
                    value={newLeaveType.noOfYears}
                    onChange={(e) =>
                      handleNewLeaveTypeChange("noOfYears", e.target.value)
                    }
                    min="0"
                  />
                </div>

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={newLeaveType.carryForward}
                      onChange={(e) =>
                        handleNewLeaveTypeChange(
                          "carryForward",
                          e.target.checked
                        )
                      }
                    />
                    Carry Forward
                  </label>
                </div>

                {newLeaveType.carryForward && (
                  <div className="form-group">
                    <label>Carry Forward Days</label>
                    <input
                      type="number"
                      value={newLeaveType.carryForwardDays}
                      onChange={(e) =>
                        handleNewLeaveTypeChange(
                          "carryForwardDays",
                          e.target.value
                        )
                      }
                      min="0"
                    />
                  </div>
                )}

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={newLeaveType.encashment}
                      onChange={(e) =>
                        handleNewLeaveTypeChange("encashment", e.target.checked)
                      }
                    />
                    Encashment
                  </label>
                </div>

                {newLeaveType.encashment && (
                  <div className="form-group">
                    <label>Encashment Days</label>
                    <input
                      type="number"
                      value={newLeaveType.encashmentDays}
                      onChange={(e) =>
                        handleNewLeaveTypeChange(
                          "encashmentDays",
                          e.target.value
                        )
                      }
                      min="0"
                    />
                  </div>
                )}

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={newLeaveType.optionalHoliday}
                      onChange={(e) =>
                        handleNewLeaveTypeChange(
                          "optionalHoliday",
                          e.target.checked
                        )
                      }
                    />
                    Optional Holiday
                  </label>
                </div>
              </div>

              <div className="form-actions">
                <ButtonWithIcon
                  className="cancel-button"
                  icon="mdi:close"
                  text="Cancel"
                  onClick={() => setShowAddForm(false)}
                />
                <ButtonWithIcon
                  className="add-button"
                  icon="mdi:check"
                  text="Add Leave Type"
                  onClick={handleAddLeaveType}
                />
              </div>
            </div>
          )}

          <div className="leave-actions">
            {!showAddForm && (
              <ButtonWithIcon
                className="add-new-button"
                icon="mdi:plus"
                text="Add Leave Type"
                onClick={() => setShowAddForm(true)}
              />
            )}

            {leaveData.length > 0 && (
              <ButtonWithIcon
                className="submit-button"
                icon="mdi:content-save"
                text={loading ? "Saving..." : "Save All Changes"}
                onClick={handleSubmitAll}
                disabled={loading}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmployeeLeaveBalancePopup;
